import { useState } from 'react';
import { useEmergencyStore, getStatusColor } from '@/stores/emergency-store';
import { useAuthStore } from '@/stores/auth-store';
import { TicketChatPanel } from '@/components/emergency/TicketChatPanel';
import type { Alert, Patrol } from '@/stores/emergency-store';
import type { TicketStatus } from '@asi/shared';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  AlertTriangle,
  Flame,
  Heart,
  Shield,
  AlertCircle,
  MapPin,
  Clock,
  User,
  Phone,
  ChevronLeft,
  Navigation,
  CheckCircle,
  XCircle,
  Motorbike,
} from 'lucide-react';

function getAlertIcon(type: Alert['type']) {
  switch (type) {
    case 'SOS':
      return <AlertTriangle className="w-6 h-6" />;
    case 'FIRE':
      return <Flame className="w-6 h-6" />;
    case 'MEDICAL':
      return <Heart className="w-6 h-6" />;
    case 'INTRUSION':
      return <Shield className="w-6 h-6" />;
    case 'PANIC':
      return <AlertCircle className="w-6 h-6" />;
    default:
      return <AlertCircle className="w-6 h-6" />;
  }
}

function getAlertTypeName(type: Alert['type']) {
  const names: Record<string, string> = {
    SOS: 'Emergencia SOS',
    FIRE: 'Incendio',
    MEDICAL: 'Emergencia Médica',
    INTRUSION: 'Intrusión',
    PANIC: 'Botón de Pánico',
  };
  return names[type] ?? type;
}

function getAlertTypeColor(type: Alert['type']) {
  switch (type) {
    case 'SOS':
      return 'bg-red-500';
    case 'FIRE':
      return 'bg-orange-500';
    case 'MEDICAL':
      return 'bg-pink-500';
    case 'INTRUSION':
      return 'bg-purple-500';
    case 'PANIC':
      return 'bg-red-600';
    default:
      return 'bg-gray-500';
  }
}

function getStatusName(status: Alert['status']) {
  const names: Record<string, string> = {
    PENDING: 'Pendiente',
    ASSIGNED: 'Asignada',
    EN_ROUTE: 'En camino',
    ON_SCENE: 'En sitio',
    IN_PROGRESS: 'En proceso',
    RESOLVED: 'Finalizada',
    CLOSED: 'Cerrada',
    CANCELLED: 'Cancelada',
  };
  return names[status] ?? status;
}

export function AlertDetail() {
  const {
    selectedAlert,
    selectAlert,
    patrols,
    assignPatrolToAlert,
    updateAlertStatus,
  } = useEmergencyStore();

  const [showAssign, setShowAssign] = useState(false);
  const [assigningPatrol, setAssigningPatrol] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const operatorId = useAuthStore((s) => s.session?.user.id ?? null);

  if (!selectedAlert) return null;

  const statusColors = getStatusColor(selectedAlert.status);
  // For the assign-patrol dropdown we show patrollers that can take the
  // call: AVAILABLE (live + idle) or OFFLINE (no recent GPS or app closed —
  // operator confirms by phone). Patrols already on another call (ASSIGNED /
  // EN_ROUTE / ON_SCENE) are hidden to avoid double-assignment.
  const availablePatrols = patrols.filter(
    (p) => p.status === 'AVAILABLE' || p.status === 'BACKGROUND' || p.status === 'OFFLINE',
  );

  const handleAssignPatrol = async (patrol: Patrol) => {
    setAssigningPatrol(true);
    await assignPatrolToAlert(selectedAlert.id, patrol.id);
    setAssigningPatrol(false);
    setShowAssign(false);
  };

  const handleUpdateStatus = async (status: TicketStatus) => {
    await updateAlertStatus(selectedAlert.id, status, resolutionNotes);
    if (status === 'RESOLVED' || status === 'CLOSED') {
      selectAlert(null);
    }
    setShowResolve(false);
    setResolutionNotes('');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header with back button */}
      <div className="p-4 border-b border-gray-200 dark:border-slate-800 flex items-center gap-3">
        <button
          onClick={() => selectAlert(null)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-200 dark:bg-slate-800 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-slate-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white">Alerta #{selectedAlert.ticket_number}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${statusColors.bg} ${statusColors.text}`}>
              {getStatusName(selectedAlert.status)}
            </span>
          </div>
        </div>
      </div>

      {/* Primary actions — moved to top so the operator can act without scrolling. */}
      <div className="p-3 border-b border-gray-200 dark:border-slate-800 bg-gray-100 dark:bg-slate-900/40 space-y-2">
        {selectedAlert.status === 'PENDING' && !showAssign && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setShowAssign(true)}
              className="py-2.5 bg-blue-600 text-gray-900 dark:text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Motorbike className="w-4 h-4" />
              Asignar Patrullero
            </button>
            <button
              onClick={() => handleUpdateStatus('CANCELLED')}
              className="py-2.5 bg-gray-200 dark:bg-slate-800 text-red-400 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-200 dark:bg-slate-700 transition-colors flex items-center justify-center gap-2 text-sm border border-red-500/30"
            >
              <XCircle className="w-4 h-4" />
              Cancelar Alerta
            </button>
          </div>
        )}
        {selectedAlert.status === 'PENDING' && showAssign && (
          <div className="space-y-2">
            <p className="text-gray-600 dark:text-slate-400 text-xs">Seleccionar patrullero:</p>
            {availablePatrols.length === 0 ? (
              <p className="text-amber-400 text-xs">No hay patrulleros disponibles</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {availablePatrols.map((patrol) => {
                  const isOffline = patrol.status === 'OFFLINE';
                  return (
                  <button
                    key={patrol.id}
                    onClick={() => handleAssignPatrol(patrol)}
                    disabled={assigningPatrol}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-200 dark:bg-slate-800 transition-colors disabled:opacity-50"
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isOffline ? 'bg-slate-500/20' : 'bg-green-500/20'}`}>
                      <Motorbike className={`w-4 h-4 ${isOffline ? 'text-slate-400' : 'text-green-400'}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-gray-900 dark:text-white text-sm">{patrol.name}</p>
                      {isOffline && (
                        <p className="text-[10px] text-amber-400">Sin GPS — confirmar por llamada</p>
                      )}
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => setShowAssign(false)}
              className="w-full py-1.5 text-gray-600 dark:text-slate-400 text-xs hover:text-white"
            >
              Cancelar
            </button>
          </div>
        )}
        {selectedAlert.status === 'ASSIGNED' && (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleUpdateStatus('EN_ROUTE')}
              className="py-2.5 bg-blue-600 text-gray-900 dark:text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Navigation className="w-4 h-4" />
              Marcar En Camino
            </button>
            <button
              onClick={() => handleUpdateStatus('CANCELLED')}
              className="py-2.5 bg-gray-200 dark:bg-slate-800 text-red-400 font-medium rounded-lg hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2 text-sm border border-red-500/30"
            >
              <XCircle className="w-4 h-4" />
              Cancelar Alerta
            </button>
          </div>
        )}
        {selectedAlert.status === 'EN_ROUTE' && (
          <button
            onClick={() => handleUpdateStatus('ON_SCENE')}
            className="w-full py-2.5 bg-green-600 text-gray-900 dark:text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <MapPin className="w-4 h-4" />
            Marcar En Sitio
          </button>
        )}
        {(selectedAlert.status === 'ON_SCENE' || selectedAlert.status === 'IN_PROGRESS') && !showResolve && (
          <button
            onClick={() => setShowResolve(true)}
            className="w-full py-2.5 bg-green-600 text-gray-900 dark:text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <CheckCircle className="w-4 h-4" />
            Finalizar Alerta
          </button>
        )}
        {(selectedAlert.status === 'ON_SCENE' || selectedAlert.status === 'IN_PROGRESS') && showResolve && (
          <div className="space-y-2">
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Notas de finalización (opcional)..."
              className="w-full p-2 bg-gray-200 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-white placeholder:text-gray-500 dark:text-slate-500 resize-none text-sm"
              rows={2}
            />
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowResolve(false)}
                className="py-2 text-gray-600 dark:text-slate-400 text-sm hover:text-white border border-gray-200 dark:border-slate-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleUpdateStatus('RESOLVED')}
                className="py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Alert info */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Type card */}
        <div
          className={`${getAlertTypeColor(selectedAlert.type)} rounded-xl p-4 flex items-center gap-4`}
        >
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center text-white">
            {getAlertIcon(selectedAlert.type)}
          </div>
          <div>
            <h3 className="text-gray-900 dark:text-white font-bold text-lg">
              {getAlertTypeName(selectedAlert.type)}
            </h3>
            <p className="text-white/80 text-sm">
              {format(new Date(selectedAlert.created_at), "dd/MM/yyyy HH:mm:ss")}
            </p>
          </div>
        </div>

        {/* Cuenta / Sitio (panel-originated tickets) — operators MUST verify
            identity over the phone using the verbal password (clave) before
            dispatching a patrol, so we surface it prominently when present. */}
        {(selectedAlert.account_number || selectedAlert.site_name) && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/40 rounded-xl p-4 space-y-3">
            <h4 className="text-amber-700 dark:text-amber-400 text-sm font-medium uppercase">
              Cuenta de alarma
            </h4>
            <div className="space-y-2">
              {selectedAlert.account_number && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-slate-400">N° de cuenta</span>
                  <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                    {selectedAlert.account_number}
                  </span>
                </div>
              )}
              {selectedAlert.account_name && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-500 dark:text-slate-400">Titular</span>
                  <span className="text-sm text-right text-gray-900 dark:text-white truncate">
                    {selectedAlert.account_name}
                  </span>
                </div>
              )}
              {selectedAlert.site_name && (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-500 dark:text-slate-400">Sitio</span>
                  <span className="text-sm text-right text-gray-900 dark:text-white truncate">
                    {selectedAlert.site_name}
                  </span>
                </div>
              )}
              {selectedAlert.account_clave && (
                <div className="mt-2 p-2 rounded-md bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700/60">
                  <p className="text-[10px] uppercase font-semibold text-amber-800 dark:text-amber-300">
                    Clave de verificación
                  </p>
                  <p className="font-mono text-base font-bold text-amber-900 dark:text-amber-200">
                    {selectedAlert.account_clave}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Client info — only meaningful for mobile-client SOS alerts.
            Panel-originated tickets have no client_id, so we hide the card
            instead of showing a generic "Cliente" placeholder. */}
        {selectedAlert.client_id && (
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
          <h4 className="text-gray-600 dark:text-slate-400 text-sm font-medium uppercase">Cliente</h4>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            </div>
            <div>
              <p className="text-gray-900 dark:text-white font-medium">
                {selectedAlert.client_name ?? 'Cliente'}
              </p>
              {selectedAlert.client_phone && (
                <a
                  href={`tel:${selectedAlert.client_phone}`}
                  className="text-blue-400 text-sm flex items-center gap-1 hover:underline"
                >
                  <Phone className="w-3 h-3" />
                  {selectedAlert.client_phone}
                </a>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Location */}
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-3">
          <h4 className="text-gray-600 dark:text-slate-400 text-sm font-medium uppercase">Ubicación</h4>
          
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              {selectedAlert.address || selectedAlert.site_address ? (
                <p className="text-white">{selectedAlert.address ?? selectedAlert.site_address}</p>
              ) : (
                <p className="text-gray-600 dark:text-slate-400 italic">Sin dirección</p>
              )}
              <p className="text-gray-500 dark:text-slate-500 text-sm">
                {selectedAlert.latitude.toFixed(6)}, {selectedAlert.longitude.toFixed(6)}
              </p>
            </div>
          </div>

          {/* Google Maps link */}
          <a
            href={`https://www.google.com/maps?q=${selectedAlert.latitude},${selectedAlert.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
          >
            <Navigation className="w-4 h-4" />
            Abrir en Google Maps
          </a>
        </div>

        {/* Description */}
        {selectedAlert.description && (
          <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-2">
            <h4 className="text-gray-600 dark:text-slate-400 text-sm font-medium uppercase">Descripción</h4>
            <p className="text-white">{selectedAlert.description}</p>
          </div>
        )}

        {/* Assigned patrol */}
        {selectedAlert.patrol_name && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <h4 className="text-green-400 text-sm font-medium uppercase mb-2">Patrullero Asignado</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <Motorbike className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-900 dark:text-white font-medium">{selectedAlert.patrol_name}</p>
                <p className="text-gray-600 dark:text-slate-400 text-sm">
                  Estado: {getStatusName(selectedAlert.status)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Chat with client / patrol — only for mobile SOS tickets, not panel-originated */}
        {!selectedAlert.source_arc_event_id && (
          <TicketChatPanel ticketId={selectedAlert.id} operatorId={operatorId} />
        )}

        {/* Time info */}
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 space-y-2">
          <h4 className="text-gray-600 dark:text-slate-400 text-sm font-medium uppercase">Tiempo</h4>
          <div className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
            <Clock className="w-4 h-4" />
            <span>
              Hace{' '}
              {formatDistanceToNow(new Date(selectedAlert.created_at), {
                locale: es,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
